import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

interface IApiEnvelope<T> {
  data: T;
  message?: string;
  meta?: unknown;
}

export type IExtratoStatus =
  | 'processando'
  | 'concluido'
  | 'erro'
  | 'a_identificar'
  | 'aguardando_titular'
  | 'aguardando_validacao'
  | 'quarentena';

// Âncora do fecho do balanço: contra o que o balanço foi (ou não) validado.
// Verificado = saldo_linha | si_sf | ancora_sf. Não-verificado (derivado,
// fecha trivial SF=SI+net) = si_apenas | derivado.
export type IBalancoAncora =
  | 'saldo_linha'
  | 'si_sf'
  | 'ancora_sf'
  | 'si_apenas'
  | 'derivado'
  | 'nao_fecha'
  | 'sem_movimento'
  | 'nao_aplicavel';

export interface IExtratoCheck {
  id: string;
  label: string;
  nivel: 'ok' | 'aviso' | 'erro';
  ok: boolean;
  detalhe?: string;
}

export interface IExtratoVeredito {
  nivel: 'ok' | 'aviso' | 'erro';
  bloqueia: boolean;
  score: number;
  checks: IExtratoCheck[];
  msg?: string; // compat com criticas antigas (so msg)
}

export interface IExtratoItem {
  id: number;
  nome_arquivo: string;
  formato: string;
  status: IExtratoStatus;
  empresa_id?: number | null;
  empresa_nome?: string | null;
  banco_nome?: string | null;
  agencia?: string | null;
  conta?: string | null;
  periodo_ini?: string | null;
  periodo_fim?: string | null;
  saldo_inicial?: number | null;
  saldo_final?: number | null;
  qtd_transacoes?: number | null;
  qtd_novas?: number | null;
  qtd_duplicadas?: number | null;
  /**
   * Lançamentos vivos vinculados, contados agora pelo backend. Difere de
   * `qtd_transacoes`, que é o retrato do que o ETL respondeu na época e não
   * acompanha exclusão — extrato com 6 no cabeçalho e 0 no banco exibia
   * "6 txns · CONFIÁVEL" e dava garantia falsa.
   */
  qtd_lancamentos?: number | null;
  balanco_ok?: boolean | null;
  divergencia_balanco?: number | null;
  // Qualidade do fecho: verificado contra saldo do banco vs apenas derivado.
  // Quando balanco_ok=true mas balanco_verificado=false, o fecho é "revisar".
  balanco_verificado?: boolean | null;
  balanco_ancora?: IBalancoAncora | null;
  confianca_global?: number | null;
  cnpj_detectado?: string | null;
  titular_detectado?: string | null;
  // Tipo do PDF identificado pelo ETL. Quando difere de extrato_conta_corrente
  // / extrato_sem_movimento, exibimos badge "não é extrato" no card.
  tipo_documento?: string | null;
  layout_familia?: string | null;
  // Natureza da conta detectada pelo ETL (corrente/poupança/investimento/salário).
  tipo_conta?: string | null;
  importado_por_email?: string | null;
  /** Nome de quem subiu, do cadastro. Cai para null se o usuário foi removido —
   *  o e-mail é snapshot no import e sobrevive, o cadastro não. */
  importado_por_nome?: string | null;
  importado_por_origem?: string | null;
  importado_por_usuario_id?: number | null;
  dt_criacao?: string | null;
  critica_saldo?: IExtratoVeredito | null;
  pode_baixar: boolean;
  pode_reprocessar: boolean;
  pode_validar?: boolean;
  // Divergência NÃO discriminada pelo banco (drift de saldo sem lançamento
  // impresso — rendimento/taxa de conta remunerada, provado pelos totais
  // impressos do extrato). Chip âmbar permanente no card; pendentes > 0
  // bloqueia a conciliação até o contador resolver o quiz no detalhe.
  divergencia_categoria?: string | null;
  divergencias_pendentes?: number;
}

/** Janela de divergência não discriminada (quiz no detalhe do extrato). */
export interface IExtratoDivergencia {
  de?: string | null; // dd/mm/aaaa
  ate?: string | null;
  valor: number; // >0 = rendimento; <0 = taxa
  saldo_antes?: number | null;
  saldo_depois?: number | null;
  prova_totais?: boolean;
  status: 'pendente' | 'ignorado' | 'rotulado';
  resolvido_por?: string | null;
  dt_resolucao?: string | null;
  lancamento_id?: number | null;
}

export interface IExtratoResumo {
  total: number;
  concluido: number;
  processando: number;
  erro: number;
  a_identificar: number;
  aguardando_titular?: number;
  aguardando_validacao?: number;
  /** Parse não concluído pela Lambda. Não tinha contador nem card: ficava dentro
   *  do `total` e fora dos seis KPIs. */
  quarentena?: number;
}

export interface IExtratoPage {
  items: IExtratoItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
  resumo: IExtratoResumo;
}

export interface IExtratoDetail extends IExtratoItem {
  qtd_lancamentos: number;
  erro_processamento?: string | null;
  divergencias?: IExtratoDivergencia[] | null;
}

export interface IExtratoLancamento {
  id: number;
  data: string; // YYYY-MM-DD
  historico: string;
  documento?: string | null;
  valor: number;
  dc: 'D' | 'C';
  saldo?: number | null;
  categoria?: string | null;
  contraparte?: string | null;
  status: string;
}

export interface IExtratoLancamentosPage {
  items: IExtratoLancamento[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface IExtratosFiltro {
  empresaId?: number;
  status?: string;
  banco?: string;
  search?: string;
  ordem?: 'recentes' | 'antigos';
  dateIni?: string; // ISO; ausente => todos os períodos
  dateFim?: string; // ISO; ausente => todos os períodos
  usuarioId?: number; // filtra por quem importou (tb_usuario.id)
  origem?: string; // 'escritorio' | 'cliente'
  page: number;
  size: number;
}

/**
 * Lista paginada de extratos importados (tela Extratos). Diferente das outras
 * telas do módulo, busca sem empresa (lista todas as permitidas). Mantém a
 * página anterior durante o refetch e repolla a cada 3s se houver algum
 * extrato ainda processando.
 */
export const useContaCilExtratos = (filtro: IExtratosFiltro) =>
  useQuery({
    queryKey: ['contacil', 'extratos', filtro] as const,
    placeholderData: keepPreviousData,
    // O status entra na chave, mas o resumo dos KPIs e global. Com o staleTime
    // de 1 min do queryConfig, voltar a um card servia a contagem velha.
    staleTime: 0,
    queryFn: async (): Promise<IExtratoPage> => {
      const params: Record<string, string | number> = {
        page: filtro.page,
        size: filtro.size,
      };
      if (filtro.empresaId != null) params.empresa_id = filtro.empresaId;
      if (filtro.status) params.status = filtro.status;
      if (filtro.banco) params.banco = filtro.banco;
      if (filtro.search) params.search = filtro.search;
      if (filtro.ordem) params.ordem = filtro.ordem;
      // Sem data => sem param => backend devolve todos os períodos.
      if (filtro.dateIni) params.date_ini = filtro.dateIni;
      if (filtro.dateFim) params.date_fim = filtro.dateFim;
      if (filtro.usuarioId != null) params.usuario_id = filtro.usuarioId;
      if (filtro.origem) params.origem = filtro.origem;
      const res = await api.get<IApiEnvelope<IExtratoPage>>('/api/v1/contacil/extratos/', {
        params,
      });
      return res.data.data;
    },
    // Pelo resumo, nao pela lista: `items` vem filtrado por status, entao no
    // card Processando vazio o polling se desligava sozinho.
    refetchInterval: query => (query.state.data?.resumo.processando ? 3000 : false),
  });

export interface IExtratoUsuarioFacet {
  usuario_id?: number | null;
  email: string;
  nome?: string | null;
}

/**
 * Usuários que importaram extratos (popula o filtro de usuário). Respeita o
 * escopo multi-tenant no backend. Cacheado por mais tempo (muda pouco).
 */
export const useContaCilExtratosUsuarios = () =>
  useQuery({
    queryKey: ['contacil', 'extratos-usuarios'] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<IExtratoUsuarioFacet[]> => {
      const res = await api.get<IApiEnvelope<IExtratoUsuarioFacet[]>>(
        '/api/v1/contacil/extratos/usuarios',
      );
      return res.data.data;
    },
  });

/**
 * Detalhe de um extrato (cabeçalho do drill-down). Só busca com o drawer aberto.
 */
export const useContaCilExtratoDetalhe = (extratoId: number | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ['contacil', 'extrato-detalhe', extratoId] as const,
    enabled: enabled && extratoId != null,
    queryFn: async (): Promise<IExtratoDetail> => {
      const res = await api.get<IApiEnvelope<IExtratoDetail>>(
        `/api/v1/contacil/extratos/${extratoId}`,
      );
      return res.data.data;
    },
    // Reprocesso com o modal aberto: sem o repoll, o detalhe (e as janelas de
    // divergência do quiz) ficava stale enquanto a LISTA atualizava a 3s —
    // o quiz reabilitava sobre janelas velhas e o clique podia mirar a
    // janela errada (o backend também valida o fingerprint de/ate).
    refetchInterval: q => (q.state.data?.status === 'processando' ? 3000 : false),
  });

/**
 * Lançamentos de um extrato (drill-down, paginado). Liga por nome de arquivo
 * no backend. Só busca com o drawer aberto.
 */
export const useContaCilExtratoLancamentos = (
  extratoId: number | undefined,
  enabled: boolean,
  page = 1,
  size = 100,
) =>
  useQuery({
    queryKey: ['contacil', 'extrato-lancamentos', extratoId, page, size] as const,
    enabled: enabled && extratoId != null,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<IExtratoLancamentosPage> => {
      const res = await api.get<IApiEnvelope<IExtratoLancamentosPage>>(
        `/api/v1/contacil/extratos/${extratoId}/lancamentos`,
        { params: { page, size } },
      );
      return res.data.data;
    },
  });

export interface IExtratoAcaoResult {
  id: number;
  status: string;
  mensagem: string;
  lancamentos_afetados: number;
}

/**
 * Re-enfileira o parsing do extrato no ETL. Só funciona com o arquivo ainda no
 * S3 (s3_key persistido + dentro do lifecycle de 7 dias).
 */
/**
 * Invalida TODOS os caches que dependem de extratos. Padrao usado por
 * useContaCilContasBancarias / useContaCilRegras — centraliza pra que
 * cada call-site nao reinvente (e esqueca downstream).
 *
 * Cobre: extratos (lista), extrato-detalhe (modal), extrato-lancamentos
 * (drill-down), extrato-correcoes (historico), empresas-saude (KPIs %
 * conciliado / pendente), lancamentos (Conciliar) e lancamentos-resumo
 * (KPIs do Conciliar) — qualquer acao que mexe num extrato propaga.
 */
const useInvalidarExtratos = () => {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['contacil', 'extratos'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'extrato-detalhe'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'extrato-lancamentos'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'extrato-correcoes'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'empresas-saude'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'lancamentos'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'lancamentos-resumo'] });
  };
};

export const useReprocessarExtrato = () => {
  const invalidar = useInvalidarExtratos();
  return useMutation({
    mutationFn: async (extratoId: number): Promise<IExtratoAcaoResult> => {
      const res = await api.post<IApiEnvelope<IExtratoAcaoResult>>(
        `/api/v1/contacil/extratos/${extratoId}/reprocessar`,
      );
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};

/**
 * Soft-delete do extrato + seus lançamentos (cascata por nome de arquivo).
 * Reversível no banco.
 */
export const useExcluirExtrato = () => {
  const invalidar = useInvalidarExtratos();
  return useMutation({
    mutationFn: async (extratoId: number): Promise<IExtratoAcaoResult> => {
      const res = await api.delete<IApiEnvelope<IExtratoAcaoResult>>(
        `/api/v1/contacil/extratos/${extratoId}`,
      );
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};

/**
 * Pega a URL presigned do arquivo original e abre numa nova aba (download).
 */
export const baixarExtrato = async (
  extratoId: number,
  inline = false,
): Promise<{ url: string; nome_arquivo: string }> => {
  const res = await api.get<IApiEnvelope<{ url: string; nome_arquivo: string }>>(
    `/api/v1/contacil/extratos/${extratoId}/download`,
    { params: inline ? { inline: true } : undefined },
  );
  return res.data.data;
};

/** URL inline do PDF do extrato que originou um lançamento (preview no Conciliar). */
export const baixarPdfLancamento = async (
  lancamentoId: number,
): Promise<{ url: string; nome_arquivo: string }> => {
  const res = await api.get<IApiEnvelope<{ url: string; nome_arquivo: string }>>(
    `/api/v1/contacil/extratos/por-lancamento/${lancamentoId}`,
  );
  return res.data.data;
};

/**
 * Valida e LIBERA um extrato retido (aguardando_validacao): materializa os
 * lançamentos (passam a fluir pro Conciliar) e registra a liberação.
 */
export const useValidarExtrato = () => {
  const invalidar = useInvalidarExtratos();
  return useMutation({
    mutationFn: async (params: {
      extratoId: number;
      observacao?: string;
    }): Promise<IExtratoAcaoResult> => {
      const res = await api.post<IApiEnvelope<IExtratoAcaoResult>>(
        `/api/v1/contacil/extratos/${params.extratoId}/validar`,
        { observacao: params.observacao ?? null },
      );
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};

/**
 * Corrige metadados (agência/banco/conta) de um extrato retido e re-audita.
 * NÃO materializa — só atualiza o veredito. Combine com useValidarExtrato
 * para "corrigir e liberar".
 */
export const useCorrigirExtrato = () => {
  const invalidar = useInvalidarExtratos();
  return useMutation({
    mutationFn: async (params: {
      extratoId: number;
      agencia?: string;
      bancoNome?: string;
      conta?: string;
      observacao?: string;
    }): Promise<IExtratoAcaoResult> => {
      const res = await api.patch<IApiEnvelope<IExtratoAcaoResult>>(
        `/api/v1/contacil/extratos/${params.extratoId}/corrigir`,
        {
          agencia: params.agencia ?? null,
          banco_nome: params.bancoNome ?? null,
          conta: params.conta ?? null,
          observacao: params.observacao ?? null,
        },
      );
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};

export interface IDivergenciaResolverResult {
  id: number;
  indice: number;
  status: 'ignorado' | 'rotulado';
  divergencias_pendentes: number;
  conciliacao_liberada: boolean;
  lancamento_id?: number | null;
  mensagem: string;
}

/**
 * Resolve UMA janela de divergência não discriminada (quiz do contador).
 * 'ignorar' registra a ciência; 'rotular' cria um lançamento com o valor da
 * janela (histórico editável, data default = fim da janela). Quando a última
 * janela é resolvida o backend libera a conciliação dos lançamentos.
 */
export const useResolverDivergencia = () => {
  const invalidar = useInvalidarExtratos();
  return useMutation({
    mutationFn: async (params: {
      extratoId: number;
      indice: number;
      acao: 'ignorar' | 'rotular';
      historico?: string;
      data?: string; // YYYY-MM-DD
      debitoPoc?: string;
      creditoPoc?: string;
      // fingerprint da janela (datas dd/mm/aaaa como exibidas): o backend
      // recusa 409 se a lista mudou (reprocesso) — evita resolver a errada
      de?: string | null;
      ate?: string | null;
    }): Promise<IDivergenciaResolverResult> => {
      const res = await api.post<IApiEnvelope<IDivergenciaResolverResult>>(
        `/api/v1/contacil/extratos/${params.extratoId}/divergencias/${params.indice}/resolver`,
        {
          acao: params.acao,
          historico: params.historico ?? null,
          data: params.data ?? null,
          debito_poc: params.debitoPoc ?? null,
          credito_poc: params.creditoPoc ?? null,
          de: params.de ?? null,
          ate: params.ate ?? null,
        },
      );
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};

export interface IExtratoCorrecao {
  id: number;
  tipo: string;
  campo?: string | null;
  valor_antes?: string | null;
  valor_depois?: string | null;
  veredito_nivel?: string | null;
  observacao?: string | null;
  usuario_email?: string | null;
  dt_criacao?: string | null;
}

/** Histórico de correções/liberações de um extrato (auditoria do ETL). */
export const useExtratoCorrecoes = (extratoId: number | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ['contacil', 'extrato-correcoes', extratoId] as const,
    enabled: enabled && extratoId != null,
    queryFn: async (): Promise<IExtratoCorrecao[]> => {
      const res = await api.get<IApiEnvelope<{ itens: IExtratoCorrecao[] }>>(
        `/api/v1/contacil/extratos/${extratoId}/correcoes`,
      );
      return res.data.data.itens;
    },
  });
